import { MENU_CODE } from '@/constants/menu'
import { fullyDecodeRouteParam, normalizeTagValue } from '~/utils/route'
import { resolveTenantHost } from '~/utils/resolveTenantHost'

export default () => {
  const { localePath } = useLanguage()

  const encodePathSegment = (value: string) => encodeURIComponent(fullyDecodeRouteParam(value))

  // Mocked store for menus - replace this with Pinia or real data
  const menuStore = useMenuStore()
  const menus = computed(() =>
    menuStore.list.filter(menu => menu.show && !menu.isSubMenu),
  )

  const slugPageInfo = computed(() => {
    if (localePath.name === '/') {
      return menuStore.list.find(menu => menu.show && menu.code === MENU_CODE.HOME)
    }
    const normalizedSlug = fullyDecodeRouteParam(String(route.params.slug || ''))
    return menuStore.list.find(menu => menu.show && fullyDecodeRouteParam(menu.slug) === normalizedSlug)
  })

  // Helper to determine path
  const getPath = ({
    componentName,
    categorySlug,
    tagSlug,
    slug,
    subMenuOf,
  }: LocalePath): string => {
    if (subMenuOf) { return localePath(`/${encodePathSegment(slug || '')}`) }

    const menu = menuStore.allList.find((l: Menu) => l.componentName === componentName)

    let path = '/'
    if (menu) {
      const menuSlug = encodePathSegment(menu.slug)
      path = `/${menuSlug}`
      if (categorySlug && !slug) {
        path = `/${menuSlug}/category/${encodePathSegment(categorySlug)}`
      } else if (tagSlug && !slug) {
        path = `/${menuSlug}/tags/${encodePathSegment(normalizeTagValue(tagSlug))}`
      } else if (!categorySlug && slug) {
        const encodedSlug = encodePathSegment(slug)
        path = path === '' ? `/${encodedSlug}` : `${path}/${encodedSlug}`
      } else if (categorySlug && slug) {
        path = `/${menuSlug}/${encodePathSegment(categorySlug)}/${encodePathSegment(slug)}`
      }
    }

    return localePath(path)
  }

  const redirectPath = (componentName: string, slug: string, code: string) => {
    if (code === MENU_CODE.GALLERY_REVIEW) { return '' }
    return localePath(!componentName && slug !== '' ? '/' : `/${slug}`)
  }

  const getPageInfo = (id: string) => menuStore.list.find((l: Menu) => l.code === id && l.show === true)

  const reqUrl = useRequestURL()
  const requestHeaders = useRequestHeaders()
  const forwardedProto = String(requestHeaders['x-forwarded-proto'] || '')
    .split(',')[0]
    .trim()
    .replace(/:$/, '')
  const protocol = import.meta.server
    ? (forwardedProto || (process.env.NODE_ENV === 'production' ? 'https' : reqUrl.protocol.replace(/:$/, '')))
    : reqUrl.protocol.replace(/:$/, '')
  const tenantHost = resolveTenantHost([
    String(requestHeaders['x-original-host'] || ''),
    String(requestHeaders['x-forwarded-host'] || ''),
    String(requestHeaders.host || ''),
    reqUrl.host,
  ]) || reqUrl.host
  const domain = computed(() => `${protocol}://${tenantHost}`)

  const route = useRoute()
  const getBreadcrumb = (title?: string): BreadCrumb[] => {
    const normalizedRoutSlug = fullyDecodeRouteParam(String(route.params.slug || ''))
    const homeMenu = menuStore.list.find((l: Menu) => l.code === MENU_CODE.HOME) || { slug: '', title: '' }
    const currentMenu = menuStore.list.find((l: Menu) => fullyDecodeRouteParam(l.slug) === normalizedRoutSlug) || { slug: '', title: '' }
    if (title) {
      const breadcrumbItem: BreadCrumb[] = [
        {
          path: localePath('/'),
          url: `${domain.value}${localePath(homeMenu.slug)}`,
          title: homeMenu.title,
        },
        {
          path: localePath(`/${currentMenu.slug}`),
          url: `${domain.value}${localePath(`/${currentMenu.slug}`)}`,
          title: currentMenu.title,
        },
        {
          title,
        },
      ]
      return breadcrumbItem
    } else {
      const breadcrumbItem: BreadCrumb[] = [
        {
          path: localePath('/'),
          url: `${domain.value}${localePath(`/${homeMenu.slug}`)}`,
          title: homeMenu.title,
        },
        {
          title: currentMenu.title,
        },
      ]
      return breadcrumbItem
    }
  }

  return {
    getBreadcrumb,
    redirectPath,
    getPageInfo,
    getPath,
    domain,
    menus,
    slugPageInfo,
  }
}
